import "../../styles/voice-animation.css";

import React from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { tallyTokens } from "../../theme.tsx";
import {
  interruptVoice,
  isVoiceBusy,
  useVoiceStatus,
} from "../../voice/voiceStatus.ts";
import { noticeLabel } from "./voiceLabels.ts";
import VoiceParticles from "./VoiceParticles.tsx";

/**
 * A running voice session, drawn as one large orb above the composer.
 *
 * The orb is the Tally particle mark, and state is carried by its motion (see
 * VoiceParticles). With reduced motion it is a still sphere, and screen
 * readers get the state from VoiceModeHost.
 * Tapping it while Tally is busy stops the response, as talking over it does.
 * Renders nothing outside voice mode.
 */
const VoiceOrb: React.FC = () => {
  const state = useVoiceStatus((status) => status.state);
  const notice = useVoiceStatus((status) => status.notice);
  const particles = !useMediaQuery("(prefers-reduced-motion: reduce)");

  if (state === "idle") return null;

  const busy = isVoiceBusy(state);

  return (
    <Box
      sx={{
        position: "absolute",
        left: "50%",
        // The particle canvas leaves room around the ring for its wobble;
        // pull it down by that margin so the ring sits where the sphere did.
        bottom: particles
          ? { xs: "calc(100% + 16px)", sm: "calc(100% + 8px)" }
          : "calc(100% + 40px)",
        transform: "translateX(-50%)",
        // The orb floats over the conversation; only the orb itself is a target.
        pointerEvents: "none",
      }}
    >
      {particles && (
        // Beside the orb rather than in it: the orb switches between a button
        // and a div with the state, and that would remount the particles and
        // replay their opening at every turn.
        <Box sx={{ position: "absolute", inset: 0 }}>
          <VoiceParticles />
        </Box>
      )}
      <Box
        component={busy ? "button" : "div"}
        type={busy ? "button" : undefined}
        onClick={busy ? interruptVoice : undefined}
        aria-label={
          busy
            ? i18n._(
                msg({
                  message: "Stop speaking",
                  context: "Voice mode control to interrupt playback",
                }),
              )
            : undefined
        }
        aria-hidden={busy ? undefined : true}
        className={
          particles ? "voiceOrbLarge voiceOrbParticles" : "voiceOrbLarge"
        }
        sx={{
          display: "block",
          width: particles ? { xs: 124, sm: 168 } : { xs: 76, sm: 104 },
          height: particles ? { xs: 124, sm: 168 } : { xs: 76, sm: 104 },
          p: 0,
          border: 0,
          pointerEvents: "auto",
          cursor: busy ? "pointer" : "default",
          "&:focus-visible": {
            outline: `2px solid ${tallyTokens.brand}`,
            outlineOffset: 4,
          },
        }}
      />
      {notice && (
        <Typography
          variant="caption"
          sx={{
            // Under the orb without moving it: the orb is anchored, not stacked.
            // The particle canvas reaches lower than the sphere did, so the
            // notice comes up into its margin to stay clear of the composer.
            position: "absolute",
            top: particles
              ? { xs: "calc(100% - 16px)", sm: "calc(100% - 24px)" }
              : "calc(100% + 8px)",
            left: "50%",
            transform: "translateX(-50%)",
            color: tallyTokens.ink2,
            whiteSpace: "nowrap",
            bgcolor: tallyTokens.surface,
            px: 1.25,
            py: 0.25,
            borderRadius: 999,
          }}
        >
          {noticeLabel(notice)}
        </Typography>
      )}
    </Box>
  );
};

export default VoiceOrb;
